import React, { PropsWithChildren } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
const ProgressContainer = styled.div`
  position: relative;
  flex-shrink: 0;
`

const SVGContainer = styled.svg`
  transform: rotate(-90deg);
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`

const CircleBackground = styled.circle`
  &.tier-bronze {
    stroke: ${props => props.theme.palette.product.loyalty.bronze.darkContrast};
  }

  &.tier-silver {
    stroke: ${props => props.theme.palette.product.loyalty.silver.darkContrast};
  }

  &.tier-gold {
    stroke: ${props => props.theme.palette.product.loyalty.gold.darkContrast};
  }

  &.tier-platinum {
    stroke: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
  }
`

const CircleProgress = styled.circle`
  transition: stroke-dashoffset 0.5s ease;

  &.tier-bronze {
    stroke: ${props => props.theme.palette.product.loyalty.bronze.darkBackground};
  }

  &.tier-silver {
    stroke: ${props => props.theme.palette.product.loyalty.silver.darkBackground};
  }

  &.tier-gold {
    stroke: ${props => props.theme.palette.product.loyalty.gold.darkBackground};
  }

  &.tier-platinum {
    stroke: ${props => props.theme.palette.product.loyalty.platinum.darkBackground};
  }
`

const ProgressContent = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

interface Props {
  progress: number
  /**
   * @default neutral-four
   */
  tier: App.LoyaltyTier
  defaultCircleBackground?: string
}

const SIZE = 76

function LoyaltyAccountTileCircularProgressBar(props: PropsWithChildren<Props>) {
  const { progress, children, tier, defaultCircleBackground = 'neutral-one' } = props
  const circleCoords = Math.round(SIZE / 2)
  const circleRadius = circleCoords - 5
  const circumference = 2 * Math.PI * circleRadius
  const offset = circumference - (progress / 100) * circumference

  return (
    <ProgressContainer style={{ width: rem(SIZE), height: rem(SIZE) }}>
      <SVGContainer width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
        {/* Background Circle */}
        <CircleBackground
          className={clsx(`background-${defaultCircleBackground} tier-${tier}`)}
          cx={circleCoords}
          cy={circleCoords}
          r={circleRadius}
          strokeWidth={6}
          fill="none"
        />
        {/* Progress Circle */}
        <CircleProgress
          className={clsx(`tier-${tier}`)}
          cx={circleCoords}
          cy={circleCoords}
          r={circleRadius}
          strokeWidth={7}
          fill="none"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeLinecap="round"
          strokeDashoffset={offset}
        />
      </SVGContainer>
      {children && <ProgressContent>
        {children}
      </ProgressContent>}
    </ProgressContainer>
  )
}

export default React.memo(LoyaltyAccountTileCircularProgressBar)
